import React from 'react';
import { Button, IconButton, Popover, Stack, TextField, Tooltip, useMediaQuery, CircularProgress } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { createLogger } from '../../utils/logger';

interface ShareTripButtonProps {
  searchId?: string | null;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabledLabel?: string;
  placement?: 'top' | 'bottom';
  variant?: 'button' | 'text';
  onShare?: () => Promise<string | null>; // Called before sharing to save trip and get new search_id
}

export const ShareTripButton: React.FC<ShareTripButtonProps> = ({
  searchId,
  label = 'Share',
  size = 'medium',
  fullWidth,
  disabledLabel = 'Link available once the trip is saved',
  placement = 'bottom',
  variant = 'button',
  onShare,
}) => {
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [currentSearchId, setCurrentSearchId] = React.useState<string | null>(searchId || null);

  React.useEffect(() => {
    setCurrentSearchId(searchId || null);
  }, [searchId]);

  const shareUrl = React.useMemo(() => {
    if (typeof window === 'undefined' || !currentSearchId) {
      return '';
    }
    return `${window.location.origin}/trip/${currentSearchId}`;
  }, [currentSearchId]);

  const nativeShareSupported = React.useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return typeof navigator.share === 'function';
  }, []);

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    if (saving) return;
    const currentTarget = event.currentTarget;

    let effectiveSearchId = currentSearchId || searchId;

    // If onShare is provided, call it to save the trip and get new search_id
    if (onShare) {
      setSaving(true);
      try {
        const newSearchId = await onShare();
        if (newSearchId) {
          setCurrentSearchId(newSearchId);
          effectiveSearchId = newSearchId; // Use the new ID directly, not the state
        }
      } catch (error) {
        log.error('Failed to save trip before sharing:', error);
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    if (!effectiveSearchId) return;
    setAnchorEl(currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      log.error('Failed to copy share link', error);
    }
  };

  const handleNativeShare = async () => {
    if (!shareUrl || typeof navigator === 'undefined' || !nativeShareSupported) return;
    try {
      await navigator.share({
        url: shareUrl,
        title: 'TripGen AI itinerary',
        text: 'Check out this itinerary I planned on TripGen AI',
      });
    } catch (error) {
      log.error('Native share failed', error);
    }
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'share-trip-popover' : undefined;
  const buttonDisabled = !searchId && !onShare;

  const button = (
    <Button
      size={size}
      startIcon={saving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <ShareIcon />}
      onClick={handleOpen}
      disabled={buttonDisabled || saving}
      fullWidth={fullWidth}
      variant={variant === 'text' ? 'text' : 'outlined'}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 999,
        px: size === 'small' ? 1.5 : 2.5,
      }}
    >
      {saving ? 'Saving...' : buttonDisabled ? (isSmallScreen ? 'Link pending' : disabledLabel) : label}
    </Button>
  );

  return (
    <React.Fragment>
      {buttonDisabled ? (
        <Tooltip title={disabledLabel}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: placement, horizontal: 'center' }}
        transformOrigin={{ vertical: placement === 'top' ? 'bottom' : 'top', horizontal: 'center' }}
        PaperProps={{ sx: { borderRadius: 3, p: 2, width: 320, maxWidth: '90vw' } }}
      >
        <Stack spacing={1.5}>
          <TextField
            value={shareUrl}
            size="small"
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
          <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
            <IconButton onClick={handleCopy} edge="end" color={copied ? 'success' : 'primary'}>
              {copied ? <CheckCircleRoundedIcon /> : <ContentCopyIcon />}
            </IconButton>
          </Tooltip>
              ),
            }}
          />
          {nativeShareSupported && (
            <Button
              variant="contained"
              startIcon={<IosShareIcon />}
              onClick={handleNativeShare}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Share via device
            </Button>
          )}
        </Stack>
      </Popover>
    </React.Fragment>
  );
};
const log = createLogger('ShareTripButton');
